import { acessoPrecisaRenovar, usarSessao, type TokensSessao } from '../estado/sessao';
import { ErroDeRede, ErroDeSessao, erroDaResposta } from './erros';

/**
 * Cliente HTTP da aplicação.
 *
 * Três comportamentos que o legado não tinha, ou tinha errado:
 *
 * 1. **Renovação em vez de logout.** O interceptor Axios legado deslogava em
 *    qualquer 401 (RN-604) — como o backend assinava o token com
 *    `ignoreExpiration: true`, isso quase nunca acontecia, e quando acontecia o
 *    usuário perdia a tela. Aqui o 401 dispara a renovação e a requisição é
 *    repetida **uma única vez**.
 * 2. **Uma renovação em voo por vez** (RN-606). Uma tela que abre seis consultas
 *    em paralelo recebe seis 401 ao mesmo tempo. Sem guarda, seriam seis
 *    renovações concorrentes — e como o refresh é **rotativo**, da segunda em
 *    diante o token apresentado já estaria revogado, o backend leria isso como
 *    reuso e derrubaria a família inteira de sessões (`auth.service.ts:renovar`).
 *    O legado resolveu o caso análogo do login com a flag `isSigningIn`; aqui a
 *    guarda é uma promessa compartilhada.
 * 3. **Erro real do usuário.** Nenhuma mensagem fixa: o que a API mandou é o que
 *    a tela mostra (ver `erros.ts`).
 */

const BASE = (() => {
  const configurada: unknown = import.meta.env['VITE_API_BASE_URL'];
  // Em dev o Vite faz proxy de `/api` para a API (vite.config.ts), então o
  // caminho relativo é o padrão certo — e evita CORS.
  return typeof configurada === 'string' && configurada !== '' ? configurada : '/api';
})();

/**
 * Não há header de empresa corrente, e isso é deliberado.
 *
 * O legado mandava a empresa num header `business-id` e parte das rotas o lia
 * **sem conferir contra o token** — daí a exportação de fornecedores aceitar
 * qualquer id que o cliente escrevesse (RN-137). Três dos oito furos de
 * autorização da auditoria nasceram desse padrão.
 *
 * No Deca 2.0 a empresa viaja no caminho ou na query, onde a rota que a recebe é
 * obrigada a validá-la (`AutorizacaoService.exigirEmpresaSolicitada`). O header
 * chegou a existir aqui por herança e ficou **inerte**: nenhum controller o
 * lia. Um canal que parece funcionar e não funciona é pior que canal nenhum —
 * alguém escreveria uma rota confiando nele.
 */

export type MetodoHttp = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface OpcoesRequisicao {
  readonly metodo?: MetodoHttp;
  /** Corpo JSON. Ignorado quando há `formulario`. */
  readonly corpo?: unknown;
  /** Corpo multipart — o navegador define o `Content-Type` com o boundary. */
  readonly formulario?: FormData;
  readonly sinal?: AbortSignal;
}

/** Requisição sem `Authorization` — login, renovação, calculadora pública. */
export async function requisicaoPublica<T>(
  caminho: string,
  opcoes: OpcoesRequisicao = {},
): Promise<T> {
  const resposta = await enviar(caminho, opcoes, null);
  return interpretar<T>(resposta);
}

/**
 * Requisição autenticada, com renovação automática.
 *
 * A renovação acontece em dois momentos: antes de enviar, se o token já estiver
 * expirado (economiza um 401 previsível), e depois de um 401, uma única vez.
 */
export async function requisicaoAutenticada<T>(
  caminho: string,
  opcoes: OpcoesRequisicao = {},
): Promise<T> {
  if (usarSessao.getState().refreshToken === null) {
    throw new ErroDeSessao('Sua sessão não está ativa. Entre novamente.');
  }

  let token = usarSessao.getState().accessToken;
  if (acessoPrecisaRenovar(usarSessao.getState())) {
    token = await renovarSessao();
  }

  const resposta = await enviar(caminho, opcoes, token);
  if (resposta.status !== 401) return interpretar<T>(resposta);

  // Um 401 aqui pode ser token expirado **ou** falta de permissão de fato; nos
  // dois casos tentamos renovar uma vez. Se outra requisição já renovou
  // enquanto esta viajava, aproveitamos o token novo sem gastar outra rotação.
  const tokenAtual = usarSessao.getState().accessToken;
  const novoToken =
    tokenAtual !== null && tokenAtual !== token ? tokenAtual : await renovarSessao();

  const segundaTentativa = await enviar(caminho, opcoes, novoToken);
  return interpretar<T>(segundaTentativa);
}

/**
 * Baixa um arquivo de uma rota autenticada.
 *
 * `<a href>` puro não serve: a rota exige `Authorization`, e o navegador não
 * manda header em navegação. O caminho é buscar com o token, virar `Blob` e
 * disparar um clique sintético.
 *
 * O nome do arquivo sai do `Content-Disposition` quando o servidor manda um —
 * ele conhece o conteúdo melhor que a tela. `nomeSugerido` é o fallback.
 */
export async function baixarArquivo(
  caminho: string,
  nomeSugerido: string,
  /** Para o arquivo que nasce de um envio — o "Extrair xlsx" manda as linhas no corpo. */
  requisicao: Pick<OpcoesRequisicao, 'metodo' | 'corpo'> = {},
): Promise<void> {
  if (usarSessao.getState().refreshToken === null) {
    throw new ErroDeSessao('Sua sessão não está ativa. Entre novamente.');
  }

  let token = usarSessao.getState().accessToken;
  if (acessoPrecisaRenovar(usarSessao.getState())) token = await renovarSessao();

  let resposta = await enviar(caminho, requisicao, token);
  if (resposta.status === 401) {
    resposta = await enviar(caminho, requisicao, await renovarSessao());
  }

  if (!resposta.ok) {
    const texto = await resposta.text();
    throw erroDaResposta(
      resposta.status,
      ehHtml(texto) ? MENSAGEM_DE_HTML : texto === '' ? null : tentarJson(texto),
    );
  }

  const nome = nomeDoCabecalho(resposta.headers.get('content-disposition')) ?? nomeSugerido;
  const url = URL.createObjectURL(await resposta.blob());

  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();

  // Revogar no mesmo tick cancelaria o download em alguns navegadores; o clique
  // é assíncrono. Um tick de folga basta e não vaza a URL.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `attachment; filename="calculo.csv"` → `calculo.csv`. */
function nomeDoCabecalho(cabecalho: string | null): string | null {
  if (cabecalho === null) return null;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cabecalho);
  if (m?.[1] === undefined) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

/** Renovação em voo, compartilhada por todas as requisições (RN-606). */
let renovacaoEmVoo: Promise<string> | null = null;

/**
 * Renova a sessão e devolve o novo access token.
 *
 * Chamadas concorrentes recebem **a mesma** promessa: uma rotação só.
 */
export function renovarSessao(): Promise<string> {
  renovacaoEmVoo ??= executarRenovacao().finally(() => {
    renovacaoEmVoo = null;
  });
  return renovacaoEmVoo;
}

async function executarRenovacao(): Promise<string> {
  const { refreshToken, encerrar, aplicarRenovacao } = usarSessao.getState();

  if (refreshToken === null) {
    encerrar('Sua sessão expirou. Entre novamente.');
    throw new ErroDeSessao();
  }

  let tokens: TokensSessao;
  try {
    tokens = await requisicaoPublica<TokensSessao>('/auth/token', {
      metodo: 'POST',
      corpo: { refreshToken },
    });
  } catch (erro) {
    // Rede fora não é sessão inválida: derrubar o login porque o Wi-Fi caiu
    // faria o usuário digitar a senha de novo por um problema que não é dele.
    if (erro instanceof ErroDeRede) throw erro;

    encerrar('Sua sessão expirou. Entre novamente.');
    throw new ErroDeSessao();
  }

  if (typeof tokens?.accessToken !== 'string' || typeof tokens?.refreshToken !== 'string') {
    encerrar('O servidor devolveu uma sessão em formato inesperado.');
    throw new ErroDeSessao('O servidor devolveu uma sessão em formato inesperado.');
  }

  // Refresh rotativo: o anterior já foi invalidado no servidor, guardar o novo
  // não é otimização — é a única forma de a próxima renovação funcionar.
  aplicarRenovacao(tokens);

  const novo = usarSessao.getState().accessToken;
  if (novo === null) throw new ErroDeSessao('O servidor devolveu um token ilegível.');
  return novo;
}

async function enviar(
  caminho: string,
  opcoes: OpcoesRequisicao,
  token: string | null,
): Promise<Response> {
  const cabecalhos = new Headers({ Accept: 'application/json' });

  let corpo: BodyInit | undefined;
  if (opcoes.formulario !== undefined) {
    corpo = opcoes.formulario;
  } else if (opcoes.corpo !== undefined) {
    cabecalhos.set('Content-Type', 'application/json');
    corpo = JSON.stringify(opcoes.corpo);
  }

  if (token !== null) cabecalhos.set('Authorization', `Bearer ${token}`);

  try {
    return await fetch(`${BASE}${caminho}`, {
      method: opcoes.metodo ?? 'GET',
      headers: cabecalhos,
      ...(corpo === undefined ? {} : { body: corpo }),
      ...(opcoes.sinal === undefined ? {} : { signal: opcoes.sinal }),
    });
  } catch (causa) {
    // Cancelamento é intenção de quem chamou (troca de tela, novo filtro), não
    // falha de rede: passa direto para o TanStack Query tratar.
    if (causa instanceof DOMException && causa.name === 'AbortError') throw causa;
    throw new ErroDeRede(causa);
  }
}

async function interpretar<T>(resposta: Response): Promise<T> {
  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();

  /**
   * Página HTML nunca é resposta válida — nem com 2xx.
   *
   * A checagem vem antes do status porque um proxy mal apontado devolve a
   * **página inicial** do outro aplicativo, com 200. Tratada como sucesso, ela
   * viraria o payload: um login "bem-sucedido" sem `accessToken`, e o defeito
   * apareceria três telas adiante, longe da causa.
   */
  if (ehHtml(texto)) throw erroDaResposta(resposta.status, MENSAGEM_DE_HTML);

  const corpo: unknown = texto === '' ? null : tentarJson(texto);

  if (!resposta.ok) throw erroDaResposta(resposta.status, corpo);

  return corpo as T;
}

function ehHtml(texto: string): boolean {
  return /^\s*<(!doctype|html)/i.test(texto);
}

const MENSAGEM_DE_HTML = {
  message:
    'A API respondeu com uma página HTML em vez de JSON. ' +
    'Normalmente é a requisição chegando no serviço errado — confira o alvo do proxy ' +
    '(VITE_API_URL / APP_PORT) e se a API está no ar.',
};

function tentarJson(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    /**
     * Resposta não-JSON. Preservar o conteúdo é melhor que mascarar com "erro
     * desconhecido" — mas **página HTML não é mensagem de erro**.
     *
     * Quando o proxy entrega a requisição no serviço errado, a resposta é o
     * HTML daquele aplicativo, e despejar 300 caracteres dele no balão de erro
     * mostra `<!DOCTYPE html><html lang="pt-BR" class="__variable_f367f3"...` na
     * tela de login. Quem lê conclui que a autenticação está quebrada; a causa
     * real é a requisição ter chegado a outro lugar. Aconteceu exatamente
     * assim: o alvo do proxy apontava para a porta de outro projeto.
     *
     * O texto cru continua valendo para o resto (mensagem de gateway, erro em
     * texto puro), que costuma ser curto e dizer algo.
     */
    return { message: texto.slice(0, 300) };
  }
}
